import React from 'react';
import FormField from '../shared/FormField';
import SelectField from '../shared/SelectField';
import TextAreaField from '../shared/TextAreaField';
import CheckboxField from '../shared/CheckboxField';

const InjuryDamageAssessment = ({ 
  formData, 
  updateFormData, 
  errors = {},
  loading = false 
}) => {
  const handleFieldChange = (updates) => {
    updateFormData(updates);
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Injury/Damage Assessment</h2>
            <p className="text-sm text-gray-600 mt-1">
              Report any injuries, property damage, or environmental impact from this event.
            </p>
          </div>
        </div>
      </div>

      {/* Injury Section */}
      <div className="border rounded-lg p-4 bg-red-50 border-red-200">
        <div className="flex items-center space-x-2 mb-3">
          <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <h3 className="text-lg font-medium text-red-900">Personal Injuries</h3>
        </div>

        <CheckboxField
          name="injuries_occurred"
          label="Injuries occurred during this event"
          checked={formData.injuries_occurred}
          onChange={handleFieldChange}
          description="Check if anyone was injured as a result of this event"
          disabled={loading}
          color="red"
        />

        {formData.injuries_occurred && (
          <div className="mt-4 space-y-4">
            <TextAreaField
              label="Injury Description"
              name="injury_description"
              required
              rows={4}
              value={formData.injury_description}
              onChange={handleFieldChange}
              error={errors.injury_description}
              placeholder="Describe the nature and extent of injuries:
• Who was injured
• Type and severity of injury
• Body parts affected
• Medical treatment provided
• Hospital transport required
• Work restrictions imposed"
              maxLength={1000}
              showWordCount={true}
              disabled={loading}
            />
            
            <div className="bg-red-100 border border-red-300 rounded p-3">
              <p className="text-sm text-red-800">
                <strong>Important:</strong> For any injury, ensure immediate medical attention is provided and incident is reported to management immediately.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Property Damage Section */}
      <div className="border rounded-lg p-4 bg-orange-50 border-orange-200">
        <div className="flex items-center space-x-2 mb-3">
          <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
          </svg>
          <h3 className="text-lg font-medium text-orange-900">Property Damage</h3>
        </div>

        <CheckboxField
          name="property_damage"
          label="Property damage occurred during this event"
          checked={formData.property_damage}
          onChange={handleFieldChange}
          description="Check if equipment, aircraft, facilities, or other property was damaged"
          disabled={loading}
          color="amber"
        />

        {formData.property_damage && (
          <div className="mt-4 space-y-4">
            <TextAreaField
              label="Damage Description"
              name="damage_description"
              required
              rows={4}
              value={formData.damage_description}
              onChange={handleFieldChange}
              error={errors.damage_description}
              placeholder="Describe the damage to equipment, facilities, or aircraft:
• What was damaged
• Extent of damage (minor, major, total loss)
• Impact on operations
• Repair requirements
• Replacement needs"
              maxLength={1000}
              showWordCount={true}
              disabled={loading}
            />

            <FormField
              label="Estimated Damage Cost (USD)"
              name="estimated_damage_cost"
              type="number"
              step="0.01"
              min="0"
              value={formData.estimated_damage_cost || ''}
              onChange={handleFieldChange}
              error={errors.estimated_damage_cost}
              placeholder="0.00"
              helpText="Provide your best estimate of repair/replacement costs"
              disabled={loading}
            />
          </div>
        )}
      </div>

      {/* Environmental/Spill Section */}
      <div className="border rounded-lg p-4 bg-green-50 border-green-200">
        <div className="flex items-center space-x-2 mb-3">
          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
          </svg>
          <h3 className="text-lg font-medium text-green-900">Environmental Impact</h3>
        </div>

        <CheckboxField
          name="spill_occurred"
          label="Spill or environmental release occurred"
          checked={formData.spill_occurred}
          onChange={handleFieldChange}
          description="Check if there was any spill of fuel, oil, chemicals, or other substances"
          disabled={loading}
          color="green"
        />

        {formData.spill_occurred && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField
                label="Spill Type"
                name="spill_type"
                required
                value={formData.spill_type}
                onChange={handleFieldChange}
                options={[
                  { value: 'Fuel', label: 'Fuel (Jet A1, Gasoline, etc.)' },
                  { value: 'Oil', label: 'Oil (Engine, Hydraulic, etc.)' },
                  { value: 'Hydraulic', label: 'Hydraulic Fluid' },
                  { value: 'Chemical', label: 'Chemical (Cleaning agents, etc.)' },
                  { value: 'Other', label: 'Other' }
                ]}
                error={errors.spill_type}
                placeholder="Select spill type"
                disabled={loading}
              />

              <FormField
                label="Amount (Liters)"
                name="spill_amount_liters"
                type="number"
                step="0.1"
                min="0"
                value={formData.spill_amount_liters || ''}
                onChange={handleFieldChange}
                error={errors.spill_amount_liters}
                placeholder="Estimated amount"
                helpText="Approximate volume spilled"
                disabled={loading}
              />
            </div>

            <CheckboxField
              name="spill_contained"
              label="Spill was contained and cleaned up"
              checked={formData.spill_contained}
              onChange={handleFieldChange}
              description="Check if the spill was successfully contained and cleanup completed"
              disabled={loading}
              color="green"
            />

            <TextAreaField
              label="Environmental Impact"
              name="environmental_impact"
              rows={3}
              value={formData.environmental_impact}
              onChange={handleFieldChange}
              error={errors.environmental_impact}
              placeholder="Describe any environmental impact or remediation required:
• Where the spill occurred (concrete, soil, drain, etc.)
• Cleanup actions taken
• Environmental protection measures
• Potential long-term impacts"
              maxLength={1000}
              showWordCount={true}
              disabled={loading}
            />
          </div>
        )}
      </div>

      {/* Regulatory Reporting Section */}
      <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
        <div className="flex items-center space-x-2 mb-3">
          <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd" />
          </svg>
          <h3 className="text-lg font-medium text-yellow-900">Regulatory Reporting</h3>
        </div>

        <CheckboxField
          name="regulatory_reporting_required"
          label="This event requires regulatory reporting"
          checked={formData.regulatory_reporting_required}
          onChange={handleFieldChange}
          description="Check if this event must be reported to aviation authorities (CAAT, EASA, FAA, etc.)"
          disabled={loading}
          color="amber"
        />

        {formData.regulatory_reporting_required && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField
                label="Regulatory Body"
                name="regulatory_body"
                required
                value={formData.regulatory_body}
                onChange={handleFieldChange}
                options={[
                  { value: 'CAAT', label: 'CAAT (Civil Aviation Authority of Thailand)' },
                  { value: 'EASA', label: 'EASA (European Aviation Safety Agency)' },
                  { value: 'FAA', label: 'FAA (Federal Aviation Administration)' },
                  { value: 'ICAO', label: 'ICAO (International Civil Aviation Organization)' },
                  { value: 'Other', label: 'Other' }
                ]}
                error={errors.regulatory_body}
                placeholder="Select regulatory authority"
                disabled={loading}
              />

              <FormField
                label="Regulatory Report Number"
                name="regulatory_report_number"
                type="text"
                value={formData.regulatory_report_number}
                onChange={handleFieldChange}
                error={errors.regulatory_report_number}
                placeholder="Report reference (if already filed)"
                maxLength={50}
                helpText="Enter if already filed with regulatory body"
                disabled={loading}
              />
            </div>

            <div className="bg-yellow-100 border border-yellow-300 rounded p-3">
              <p className="text-sm text-yellow-800">
                <strong>Regulatory Reporting:</strong> Events requiring regulatory reporting must be submitted within specified timeframes. Contact Safety Management immediately.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <span className="ml-2 text-sm text-gray-600">Processing assessment...</span>
        </div>
      )}
    </div>
  );
};

export default InjuryDamageAssessment;
